import { getImminentThresholdInstant, isAppointmentImminent } from "@internal/constants/telemedicine/imminence"
import { useNow } from "@internal/ui"
import { useJoinLink } from "./useAppointments"
import type { UpcomingAppointment } from "./types"

// ⚠ BOTH HALVES OF THIS HOOK'S MACHINERY ARE SHARED NOW, and neither lives here any more.
// CONAPP-353 hoisted the imminence predicates to `@internal/constants/telemedicine/imminence` and
// the two-timer `useNow` to `@internal/ui`, because `@internal/consumer`'s plan card needs the same
// line and the same clock and cannot import this package. What is left below is the composition:
// the rail's rule for resolving a join URL, which is this package's own.

export type AppointmentTiming = {
  /** One reading, feeding both the countdown and the imminence test so they cannot disagree. */
  now: Date
  imminent: boolean
  /** The record's own link, else the just-in-time fetch. Empty when there is none yet. */
  joinUrl: string
  canJoin: boolean
}

/**
 * Shared by `UpNextCard` and `HomeAppointmentsTile`. The two render nothing alike but agree exactly on
 * when a visit becomes joinable and how its link resolves, which is the part worth having once.
 *
 * Tolerates a null appointment because both callers guard *after* their hooks run.
 */
export function useAppointmentTiming(appointment: UpcomingAppointment | null | undefined): AppointmentTiming {
  // `"minutely"` because THIS rail draws the time and not just a verdict about it: the countdown
  // chip, "Starting in 9 minutes" and the "Today" label all read `now`, and each of them is wrong by
  // a minute for every minute the reading does not move. The plan card asks for the other mode — see
  // `ClockTicks` — and that difference is deliberate.
  const now = useNow(getImminentThresholdInstant(appointment?.date), "minutely")
  const imminent = isAppointmentImminent(appointment?.date, now)
  // A fixture carries its own link, so the fixture path fires no request.
  const { joinUrl: fetchedJoinUrl } = useJoinLink(appointment?.id ?? "", {
    enabled: imminent && !appointment?.joinUrl
  })

  const joinUrl = appointment?.joinUrl || fetchedJoinUrl || ""
  return { now, imminent, joinUrl, canJoin: joinUrl.length > 0 }
}
